import React, { useCallback, useEffect, useRef, useState } from 'react';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { UseAuthContext } from '../../context/AuthContext';
import FormFields from './FormFields';

const AWS_REGION = process.env.REACT_APP_AWS_REGION;
const apiKey = process.env.REACT_APP_AWS_MAP_API_KEY;

export default function CreateSingleAddress({tabName}) {
	 const { t } = useTranslation();
	 const { token } = UseAuthContext();
	// AWS map state variables
	const mapRef = useRef(null);
	const containerRef = useRef(null);
	const markerRef = useRef(null);
	const [showMap, setShowMap] = useState(false);
	//search related variables
	const [suggestion, setSuggestion] = useState('');
	const [suggestions, setSuggestions] = useState([]);

	const [creationMsg, setCreationMsg] = useState('')
	const [errMsg, setErrMsg] = useState("");
	// form feilds
	const [searchParams, setSearchParams] = useState({
		postCode: "",
		streetBuildingId: "",
		buildingSuffix: "",
		municipalityName: "",
		streetName: "",
		districtName: "",
		floorId: "",
		suiteId: "",
		region: "",
		coordinates: "",
		streetCode: ""
	});

	const [errors, setErrors] = useState({
		postCode: "",
		streetBuildingId: "",
		buildingSuffix: "",
		municipalityName: "",
		streetName: "",
		districtName: "",
		floorId: "",
		suiteId: "",
		//region: "",
		coordinates: "",
		streetCode: ""
	});

	// ---- helper function for reverse geocoding ----
	const reverseGeocode = useCallback(async (lng, lat) => {
		setCreationMsg('')
		setSearchParams({
            postCode: "",
            streetBuildingId: "",
            buildingSuffix: "",
            municipalityName: "",
            streetName: "",
            districtName: "",
            floorId: "",
            suiteId: "",
            //region: "",
            coordinates: "",
            streetCode: ""
        });

		setErrMsg('');
		setErrors({
			postCode: "",
			streetBuildingId: "",
			buildingSuffix: "",
			municipalityName: "",
			streetName: "",
			districtName: "",
			floorId: "",
			suiteId: "",
			//region: "",
			coordinates: "",
			streetCode: ""
		});
		const url = `https://places.geo.${AWS_REGION}.amazonaws.com/v2/reverse-geocode?key=${apiKey}`
		const res = await fetch(url, {
		method: 'POST',
		body: JSON.stringify({
		QueryPosition: [lng, lat]
		}),
		headers: {
			'Content-Type': 'application/json'
		}
		});

		if (!res.ok) {
		  const text = await res.text();
		  throw new Error(`HTTP ${res.status}: ${text}`);
		}

		const data = await res.json();
		if (data?.ResultItems?.[0]?.Address?.PostalCode) {
			const addressObject = data?.ResultItems?.[0]?.Address;
			if(!addressObject.streetNumber) {
				axios.post(`/api/v1/unofficial-address/lookup/streetcode`,
				{
					streetName: addressObject?.Street,
					buildingNo: addressObject?.AddressNumber,
					floorId: '',
					suiteId: '',
					postCode: addressObject.PostalCode,
					districtName: '',
					region: '',
					municipalityName: addressObject?.Locality,
					streetCode: '',
					geoCode: [lng, lat].join(",")
				}, { headers: { "Authorization": `Bearer ${token}` } })
				.then(res => {
					if (res.data?.streetCode){
					   setSearchParams({
							postCode: addressObject.PostalCode,
							streetBuildingId: addressObject?.AddressNumber,
							buildingSuffix: "",
							municipalityName: addressObject?.Locality,
							streetName: addressObject?.Street,
							districtName: "",
							floorId: "",
							suiteId: "",
							//region: addressObject?.Region?.Name,
							coordinates: [lng, lat].join(","),
							streetCode: res.data?.streetCode
						});
					}
				})
				.catch(err => {
					const errorDetails = err.response?.data;
					if (!errorDetails?.errorMsg || errorDetails?.errorMsg === 'Street not found') {
						setErrMsg(`Street does not exist so Street Code is mandatory to be entered`);
					} else if (errorDetails?.errorMsg && errorDetails?.errorMsg !== 'Street not found') {
						setErrMsg(`${errorDetails?.errorMsg}, so please enter the streetcode`);
					} else {
						setErrMsg('An internal server error occured. Pleases try again later');
					}
				})
			}
			setSearchParams({
				postCode: addressObject.PostalCode,
				streetBuildingId: addressObject?.AddressNumber,
				buildingSuffix: "",
				municipalityName: addressObject?.Locality,
				streetName: addressObject?.Street,
				districtName: "",
				floorId: "",
				suiteId: "",
				//region: addressObject?.Region?.Name,
				coordinates: [lng, lat].join(","),
				streetCode: addressObject.streetNumber || ''
			});
		}
		return data?.ResultItems?.[0]?.Title || 'Address not found';
	}, [token]);

	useEffect(() => {
		if (!showMap || mapRef.current) return; // only initialize once and only if visible
		const map = new maplibregl.Map({
		container: containerRef.current,
		style: `https://maps.geo.${AWS_REGION}.amazonaws.com/v2/styles/Standard/descriptor?key=${apiKey}&color-scheme=Light`,
		center: [12.56553, 55.67594],  // Copenhagen, Denmark
		zoom: 11,
		attributionControl: false,
		});

		mapRef.current = map;

		map.on('click', async (e) => {
			const { lng, lat } = e.lngLat;
			// Add or update marker
			if (!markerRef.current) {
				markerRef.current = new maplibregl.Marker({ color: 'blue' })
				.setLngLat([lng, lat])
				.addTo(map);
			} else {
				markerRef.current.setLngLat([lng, lat]);
			}
			
			try {
				await reverseGeocode(lng, lat);
			} catch (err) {
				setErrMsg(`Error: ${err.message || err}`);
			}
		});
		return () => map.remove();
	}, [showMap, reverseGeocode]);

	const handleInput = (e) => {
		let id = e.target.id;
		let value = e.target.value;
		setSearchParams(pre => ({ ...pre, [id]: value }));
	}

	// Validation function to handle form field validation
	const validateField = (id, value, isOptional = false) => {
		
		if (isOptional && !value) {
			return null;
		}
		/* eslint-disable no-useless-escape */
		const validationRules = {
			streetName: /^[A-Za-z0-9åøæÅØÆé\s\-]+(vej|gade|plads|bro|sti|allé|torv|bakke|stræde)?$/,
			streetBuildingId: /^[A-Za-z0-9\-\/\s\.]+$/,
			buildingSuffix: /^[A-Za-z0-9\-\/\.]+$/,
			floorId: /^[A-Za-z0-9åøæÅØÆé\s\-\/\.]{1,6}(st|etage|th|g|g1|gf)?$/,
			suiteId: /^[A-Za-z0-9\-\/\.]+$/,
			postCode: /^[0-9]{4}$/,
			districtName: /^[A-Za-zÆØÅæøåé0-9\s\-\.]+$/,
			//region: /^[A-Za-zÆØÅæøå\s\-]+$/,
			municipalityName: /^[A-Za-zÆØÅæøåé\s\-]+$/,
			streetCode: /^[A-Za-z0-9\-]+$/,
			coordinates: /^\s*-?\d+(\.\d+)?\s*,\s*-?\d+(\.\d+)?\s*$/
		};
		/* eslint-enable no-useless-escape */
		if (!value) {
			let mandatoryText = `${id} is required`;
			if (id ==='coordinates') {
				mandatoryText = `${id} are required`;
			}
			return mandatoryText.charAt(0).toUpperCase() + mandatoryText.slice(1);
		} else if (!validationRules[id].test(value)) {
			let formatError = `${id} is invalid`;
			if (id ==='coordinates') {
				formatError = `${id} are invalid`;
			}
			return formatError.charAt(0).toUpperCase() + formatError.slice(1);
		}
		return null;
	};

	const handleValidations = () => {
        const newErrors = {};
        Object.keys(searchParams).forEach(field => {
            const isOptional = field === "districtName" || "suiteId" || "floorId" || "buildingSuffix";
            const error = validateField(field, searchParams[field], isOptional);
            if (error) {
                newErrors[field] = error;
            }
        });
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0; // Returns true if no errors
    }

	const isFormValid = (searchParams.postCode!== "" &&
		searchParams.streetBuildingId!== "" &&		
		searchParams.municipalityName !== "" &&
		searchParams.streetName !== "" &&		
		//searchParams.region!== "" &&
		searchParams.streetCode !== "" &&
		searchParams.coordinates!== "" );

    const validate = () => {
		setCreationMsg('')
		setErrMsg('')
        if(handleValidations()) {
			//api call to validate the address
			axios.post(`/api/v1/unofficial-address/validate`,
            {
                
                streetName: searchParams.streetName,
				buildingNo: searchParams.streetBuildingId,
				floorId: searchParams.floorId,
				suiteId: searchParams.suiteId,
				postCode: searchParams.postCode,
				districtName: searchParams.districtName,
				region: searchParams.region || '',
				municipalityName: searchParams.municipalityName,
				streetCode: searchParams.streetCode || '',
				geoCode: searchParams.coordinates
            }, { headers: { "Authorization": `Bearer ${token}` } })
            .then(res => {
                if (res.data?.successMsg){
                   setCreationMsg(res.data.successMsg);
                }
            })
            .catch(err => {
				const errorDetails = err.response?.data;
				if (errorDetails?.errorMsg) {
			        setErrMsg(`${errorDetails?.errorMsg}`);
				} else {
					setErrMsg('An internal server error occured. Pleases try again later');
			    }
            })

        }
        
    }

	const create = () => {
		setCreationMsg('')
		setErrMsg('')
		if(handleValidations()) {
			//api call for create single address creation
			 axios.post(`/api/v1/unofficial-address/create`,
            {
                
                streetName: searchParams.streetName,
				buildingNo: searchParams.streetBuildingId,
				floorId: searchParams.floorId,
				suiteId: searchParams.suiteId,
				postCode: searchParams.postCode,
				districtName: searchParams.districtName,
				region: searchParams.region || '',
				municipalityName: searchParams.municipalityName,
				streetCode: searchParams.streetCode || '',
				geoCode: searchParams.coordinates
            }, { headers: { "Authorization": `Bearer ${token}` } })
            .then(res => {
                if (res.data?.specificAddressSuccess){
                   setCreationMsg(res.data.specificAddressSuccess);
                } else if (!res.data?.specificAddressSuccess && res.data?.specificAddressMsg) {
					setCreationMsg(res.data.specificAddressMsg);
				}
            })
            .catch(err => {
				const errorDetails = err.response?.data;
				if (errorDetails?.error) {
			        setErrMsg(`${errorDetails?.error} due to ${errorDetails?.details}`);
				} else {
					setErrMsg('An internal server error occured. Pleases try again later');
			   }
            })			
		}
			
	}

	const reset = () => {
        setSearchParams({
            postCode: "",
            streetBuildingId: "",
            buildingSuffix: "",
            municipalityName: "",
            streetName: "",
            districtName: "",
            floorId: "",
            suiteId: "",
           // region: "",
            coordinates: "",
            streetCode: ""
        });
        setErrors({
            postCode: "",
            streetBuildingId: "",
            buildingSuffix: "",
            municipalityName: "",
            streetName: "",
            districtName: "",
            floorId: "",
            suiteId: "",
            //region: "",
            coordinates: "",
            streetCode: ""
        });
		setCreationMsg('');
		if(containerRef?.current?.innerHTML)containerRef.current.innerHTML = '';
		mapRef.current = null;
		setShowMap(false);
		setErrMsg('')
		setSuggestion('')
    }

	useEffect(()=> {
		if(tabName === 'single-address') {
			reset()
		}
	}, [tabName]);

	const registerAllFields = (val) => {
        if (val) {
            let filterData = suggestions.filter(f => f.value === val);
            let fields = filterData[0] ? filterData[0].key.split(';') : '';
            if (fields.length > 2) {
                setSearchParams(pre => ({
                    ...pre,
                    streetName: fields[0] ? fields[0] : '',
                    streetBuildingId: fields[1] ? fields[1] : '',
                    postCode: fields[2] ? fields[2] : '',
                    floorId: fields[3] ? fields[3] : '',
                    suiteId: fields[4] ? fields[4] : ''
                }))
            }
        }
    }

	const handleSuggestion = (val) => {
        if (val && val.length > 3) {
            axios.get(`/api/v1/unofficial-address/suggest?input=${val}`, { headers: { "Authorization": `Bearer ${token}` } }).then(res => {
                if (res.data && res.data.length > 0) {
                    setSuggestions(res.data.map(d => {
                        return { 'key': d, 'value': d.replaceAll(';', ' ') };
                    }));
                }
            })
        }
        setSuggestion(val);
        registerAllFields(val);
    }

	return (
		<>
			<div>
				{!showMap && (
					<button onClick={() => setShowMap(true)} style={{ marginBottom: '10px' }}>
					  Show Map
					</button>
				)}

				{showMap && (
					<div
					ref={containerRef}
					id="map"
					style={{
						width: '100%',
						height: '500px',
						border: '2px solid #ddd',
						marginBottom: "20px",
					}}
					/>
				)}
			</div>
			<div className='form-group col-md-12'>
				<label htmlFor='suggestion'>Search For Suggested Address</label>
				<input list="suggestions" className='form-control form-control-sm' name="suggestion" id="suggestion"
					value={suggestion} onChange={(e) => handleSuggestion(e.target.value)} autocomplete="off" />

				<datalist id="suggestions">
					{suggestions.map((s, i) => <option key={i} value={s.value} />)}
				</datalist>

			</div>
			<FormFields handleInput={handleInput} isCreateAddress={true} searchParams={searchParams} errors={errors} showCoordinates={true}/>
			{creationMsg && <p style={{textAlign:'center', color:'green', marginBottom:'1rem'}}>{creationMsg}</p>}
			{errMsg && <p style={{textAlign:'center', color:'red', marginBottom:'1rem'}}>{errMsg}</p>}
			<div className="d-flex flex-wrap justify-content-center " style={{ gap: "1rem", overflowX: "auto" }}>
				<button
				  className="btn btn-primary" 
				  disabled={!isFormValid}
				  onClick={validate}>
					Validate
				</button>
				<button className="btn btn-primary"   disabled={!isFormValid} onClick={create}>
					Create
				</button>
				<button className="btn btn-secondary" onClick={reset}>
					{t('search.reset')}
				</button>
			</div>
		</>
	)
}